import { ResearchFooter } from '../../components/brief/research-footer';
import { IfWrites } from '../../components/write-access';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { briefSectionFor } from '@ni/shared';
import type { Analysis, RunDetail, RunSummary } from '@ni/shared';
import { usePersonRecord } from './record';
import { useResuming, useDemo } from '../../api/app-context';
import {
  useBrief,
  useCreateRun,
  useCredits,
  useCreditPrice,
  useCreditShortfall,
  useQuickLookPrice,
  useDeleteSubject,
  useRun,
  useRunStateInvalidation,
} from '../../api/hooks';
import type { BriefViewRequest } from '../../api/client';
import { analysisCaveats, analysisOfKind, analysisProse } from '../../lib/analysis-copy';
import {
  CANNOT_BE_UNDONE,
  pricedLabel,
  RAW_DATA_ACTION,
  RESEARCH_STATE_WORDS,
  SECTION_NAV,
  sectionsDoneLine,
} from '../../lib/copy';
import { formatDay, joinClauses, plural } from '../../lib/format';
import { partitionQueuedRows } from '../../lib/queued-rows';
import {
  completedResearchRun,
  isRunMoving,
  isTerminal,
  IDENTITY_MATCH_FAILED,
  isRestartOnlyRun,
  isWorkingState,
  newRunOffers,
  noResearchYet,
} from '../../lib/run-state';
import {
  finishedWhileWatching,
  RunBlock,
  RunCompleteBlock,
} from '../../components/run-strip/run-block';
import { ResearchGrid } from '../../components/brief/reading-sections';
import { ResearchAside } from '../../components/brief/research-aside';
import { useSourceIndex } from '../../components/brief/fact-sources';
import { FactSections } from '../../components/brief/fact-sections';
import { notableFacts } from '../../components/brief/research-reading';
import { FLAGGED_FINDINGS_ANCHOR, FlaggedFindings } from '../../components/brief/flagged-findings';
import { ResearchReading } from '../../components/brief/research-reading';
import { BriefViewNote, DraftInProgressNote } from '../../components/brief/brief-view-note';
import { RunHistory } from '../../components/brief/run-history';
import { DecisionLog } from '../../components/brief/decision-log';
import { DossierRefresh } from '../../components/brief/dossier-refresh';
import {
  NotesSinceBrief,
  notesChangedSinceBrief,
  notesSinceBrief,
  updateBriefBlocker,
} from '../../components/brief/update-brief';
import { LaterRuns } from '../../components/brief/later-runs';
import { Caveats } from '../../components/ui/caveats';
import { Checkbox } from '../../components/ui/checkbox';
import { ErrorLine } from '../../components/ui/error-line';
import { Modal } from '../../components/ui/modal';
import { Prose } from '../../components/ui/prose';
import { WorkingButton } from '../../components/ui/working-button';
import { EmptyLine } from '../../components/ui/empty-line';
import { Button, DangerLink } from '../../components/ui/button';
import { ConfirmModal } from '../../components/ui/confirm-modal';
import '../../components/record/record-body.css';
import './research.css';

/**
 * Research is the intelligence surface at every stage (ui-design §2): the run
 * strip and whatever decision surface the run state demands, then the brief,
 * then the quiet foot controls.
 *
 * Before any run it reads "Identity confirmation pending." over the one primary
 * action, with the intake-derived facts already below — so the tab is never
 * empty and queued rows from intake claims have a home from the first minute.
 *
 * Composition note: facts render from the **record** read, which is the superset
 * — it carries negated rows (removed things stay visible) and held claims. The
 * brief supplies the analyses, the inline queued rows and the overflow count.
 *
 * Since §3e (2026-08-31) the brief also supplies **which dossier** those are:
 * while a run is live it pins itself to the last finished one, so the page stops
 * rewriting itself under the reader, and the live draft is one link away. The
 * page never picks — it asks (`auto` or `draft`) and labels what came back.
 */

/** How long "Brief ready" stands before it gives way to the brief. */
const RUN_COMPLETE_SHOWN_MS = 4000;

/**
 * The aside's research line: one word for where the research stands, and the
 * plan's count under it while a run is working.
 */
function researchState(
  live: RunDetail | null,
  latest: RunSummary | null,
  restartOnly: boolean,
  completed: RunSummary | null,
): { word: string; note?: string | null } {
  if (live && !isTerminal(live.state)) {
    const planned = live.facet_map.planned.length;
    const done = live.facet_map.completions.filter((c) => c.round === live.current_round).length;
    switch (live.state) {
      case 'created':
        return { word: RESEARCH_STATE_WORDS.starting };
      case 'awaiting_adjudication':
        return { word: RESEARCH_STATE_WORDS.paused, note: RESEARCH_STATE_WORDS.pausedNote };
      case 'awaiting_identity_pick':
      case 'awaiting_prose':
        return { word: RESEARCH_STATE_WORDS.waiting };
      default:
        return {
          word: RESEARCH_STATE_WORDS.running,
          note:
            live.kind === 'research' && planned > 0 && live.current_round === 0
              ? sectionsDoneLine(done, planned)
              : null,
        };
    }
  }
  // The polled run first: it is the fresher read of the same run.
  if ((live ?? latest)?.resume?.status === 'queued') return { word: RESEARCH_STATE_WORDS.resuming };
  if (latest?.state === 'failed' && !restartOnly) return { word: RESEARCH_STATE_WORDS.failed };
  if (completed) {
    return {
      word: RESEARCH_STATE_WORDS.complete,
      note: completed.completed_at ? formatDay(completed.completed_at) : null,
    };
  }
  return { word: RESEARCH_STATE_WORDS.none };
}

function latestRunOf(runs: RunSummary[]): RunSummary | null {
  if (runs.length === 0) return null;
  return [...runs].sort((a, b) => b.created_at.localeCompare(a.created_at))[0] ?? null;
}

export function Research() {
  const demo = useDemo();
  const { record, subjectId } = usePersonRecord();

  const identity = record.identity;
  const latestRun = latestRunOf(record.runs);
  /* **A run the office ended itself holds nothing.** After a wrong-person answer
     the tab reads as it did before the run (ui-design §4.5), and since the Cancel
     press (owner ruling, 2026-09-10) the same is true one door over: the office
     stopped this run, so the page goes back to offering the thing that starts
     one. An unreadable identity match (2026-09-29) goes the same way: a Resume
     would ask the same engine the same question. None is work to pick back up,
     which is why they are one predicate here and one refusal on the server. */
  const restartOnly = latestRun !== null && isRestartOnlyRun(latestRun);
  /* The one restart-only failure the office did not choose, which is why its
     line stays up beside the button that starts a run again. */
  const restartLine =
    latestRun?.state === 'failed' && latestRun.error?.code === IDENTITY_MATCH_FAILED
      ? `Research failed · ${latestRun.error.message}`
      : null;
  const liveRunId =
    latestRun && latestRun.state !== 'complete' && !restartOnly ? latestRun.id : null;

  /* Whether a run is live in the sense the *brief* means (§3e) — the server's
     `isLiveRunState`: not complete, not failed. Deliberately narrower than
     `liveRunId` above, which keeps a failed run "live" so the strip can offer
     its Resume. A failed run pins nothing: the server answers `current` for it,
     and this page must reason the same way or it will wait for a pinned brief
     that is never coming. */
  const runPinsBrief = latestRun !== null && !isTerminal(latestRun.state);

  const runQuery = useRun(liveRunId);
  useRunStateInvalidation(subjectId, runQuery.data);

  /* Whether the run is actually underway — the pipeline owns it, or its
     continuation is enqueued and about to. It is the same predicate the poll
     keys off (`isRunMoving`), read here so the lede's "Run in progress" cannot
     claim movement the client has stopped watching for, and so it stays quiet
     while the run is parked on a question the strip is already asking. The mark
     is read, never made: the strip is the surface that makes and retires it. */
  const { isMarked } = useResuming();
  const liveRun = runQuery.data ?? null;
  const runUnderway = liveRun !== null && isRunMoving(liveRun, isMarked(liveRun.id, liveRun.state));

  /* Which dossier the reader has asked for (§3e option b). `auto` lets the
     server pin: `current` with no run live, `stable` with one. The state only
     ever holds the reader's *departure* from that default, and it is sent only
     while a run is live — with none there is no draft to ask for, the request
     would be answered `current` anyway, and forcing `auto` keeps the cache key
     the same one every other page uses. */
  const [briefView, setBriefView] = useState<BriefViewRequest>('auto');
  const briefViewRequest: BriefViewRequest = runPinsBrief ? briefView : 'auto';

  /* The departure is scoped to the run it was made on. When the pin ends — the
     run completed, failed, or was withdrawn — the toggle goes back to `auto`,
     because otherwise a reader who flipped to draft during one run would start
     the *next* one in draft without ever asking for it, and watch the page
     rewrite itself under them: precisely what the pin exists to stop. Clearing
     the state rather than only the request matters, since the request already
     reads `auto` with no run live and the stale departure would come back the
     moment the next run started. */
  useEffect(() => {
    if (!runPinsBrief) setBriefView('auto');
  }, [runPinsBrief]);

  const brief = useBrief(subjectId, briefViewRequest);
  const createRun = useCreateRun(subjectId);
  const credits = useCredits();
  const quickLookPrice = useQuickLookPrice();
  const researchPrice = useCreditPrice('person_research');
  const [autoContinue, setAutoContinue] = useState(false);
  /* **A search that continues into research is priced as that research**
     (credits launch spec step 2): it is not a quick look, the press holds the
     research price, and the button names both halves of what it buys. */
  const kickoffPrice = autoContinue ? researchPrice.units : quickLookPrice;
  const kickoffShort = useCreditShortfall(kickoffPrice);
  const researchShort = useCreditShortfall(researchPrice.units);
  /* The one switch point for which dossier renders. The brief decides — it is
     the read that knows whether it is pinned, and `brief.data.view` says which
     it served.

     The `record.analyses` fallback is a cold-load convenience only: the record
     is already in hand when this route mounts, so the page has prose to show
     while the brief's first fetch is in flight. But record.analyses are the
     CURRENT rows, which during a live run are that run's draft — so using them
     as the fallback with a run in flight would paint the draft, then repaint it
     away when the pinned brief lands, staging §3e's disorienting rewrite on
     every single load of a page whose whole promise is that it does not move.
     With a run live we wait for the brief instead. That wait is short and, on a
     view flip, invisible: `useBrief` keeps the previous view on screen. */
  const analyses: Analysis[] = brief.data?.analyses ?? (runPinsBrief ? [] : record.analyses);
  const personSummary = analysisOfKind(analyses, 'person_summary');
  const summary = analysisProse(personSummary);
  const hasDetailedReading = analyses.some((a) => a.kind !== 'person_summary');

  /* The two surfaces every open question renders on, partitioned in one place
     (`partitionQueuedRows`) so they stay complements: a row whose fact this page
     is already drawing renders inline at that fact, and everything else — no
     lineage, a lineage left out earlier, a lineage on **another entity's
     record** — renders in the Flagged findings section below.

     That last case is the one that made this a deadlock rather than a cosmetic
     gap, and it is not "a pair fact filed under the counterparty": the record
     read unions those in (`subject_entity_id = :id OR counterparty_entity_id =
     :id`) and they render inline perfectly well. It is a lineage with this
     subject in **neither** slot — a duplicate person entity minted mid-run, or
     an org-principal fact subjected to the org — which the record cannot carry
     at all. Those rows were counted on Home and dropped on the way here, leaving
     the office told about questions this page would not show and the rewrite
     refused for as long as they stood. */
  const activeLineages = new Set(
    record.facts.filter((fact) => fact.status === 'active').map((fact) => fact.lineage_id),
  );
  /* The record's own pending flags, which cover the same ground as the brief's
     rows and carry the page through the brief's first fetch — **except on the
     pinned view**, where the brief is not a second source but the only one. It
     withholds the live run's questions on purpose (§3f): those batch at the run
     strip's park, and letting them back in through the record would put the
     appearing-and-disappearing asks §3f killed straight back onto the one view
     whose promise is that it does not move. */
  const recordPending =
    demo || brief.data?.view === 'stable'
      ? []
      : record.facts
          .filter((fact) => fact.status === 'active' && fact.pending_adjudication)
          .map((fact) => ({
            lineage_id: fact.lineage_id,
            decision_id: fact.pending_adjudication!.decision_id,
          }));

  /* `latestRun`, not `liveRunId`: the partition withholds a run's questions only
     while that run is non-terminal, which is Home's rule. `liveRunId` keeps a
     **failed** run live so the strip can offer its Resume, and keying the
     section on it withheld a failed run's questions from the one surface that
     would have shown them — the review panel mounts at `awaiting_adjudication`
     and nowhere else. That same state is what takes a run's questions off *both*
     of this page's surfaces: while the panel is up, they are its. */
  const queued = partitionQueuedRows(
    demo ? [] : (brief.data?.queued ?? []),
    activeLineages,
    latestRun,
    recordPending,
  );
  const queuedByLineage = queued.inlineByLineage;
  const inlineDecisionIds = queued.inlineDecisionIds;
  const firstQueuedLineage = [...queuedByLineage.keys()][0] ?? null;
  const flaggedRows = queued.flagged;

  /* The topline counts **decisions on both surfaces**, not lineages: it is a
     statement about what the office still owes an answer on, and one lineage can
     carry two open questions. Counting the map's size alone was the same
     under-count the dropped rows were, in a new place. It anchors to the first
     inline row when there is one, and to the section otherwise. */
  const queuedCount = inlineDecisionIds.size + flaggedRows.length;
  const queuedAnchor = firstQueuedLineage
    ? `#fact-${firstQueuedLineage}`
    : flaggedRows.length > 0
      ? `#${FLAGGED_FINDINGS_ANCHOR}`
      : null;

  /* "Nothing on record yet." used to be `FactSections`' own answer to having no
     sections. It cannot be any more: the dossier calls that component twice, and
     a subject with only network rows would have had it printed under On record.
     The empty record is the page's statement, so the page makes it. */
  const hasRecord = record.facts.length > 0 || record.held_claims.length > 0;

  const neverResearched = noResearchYet({ analyses, runs: record.runs });

  // The data tab deep-links a lineage as `#fact-<id>`. Two things stand between
  // that and the row: react-router never scrolls to a hash, and a collapsed
  // section has no row to scroll to. So the named fact's section opens on
  // arrival, and the row is brought into view once the children have mounted.
  const { hash } = useLocation();
  const hashLineage = hash.startsWith('#fact-') ? hash.slice('#fact-'.length) : null;
  const hashFact = hashLineage
    ? (record.facts.find((fact) => fact.lineage_id === hashLineage) ?? null)
    : null;
  const hashSection = hashFact ? briefSectionFor(hashFact.fact_type) : null;

  useEffect(() => {
    if (!hashLineage) return;
    document.getElementById(`fact-${hashLineage}`)?.scrollIntoView({ block: 'center' });
  }, []);

  const completedResearch = completedResearchRun(record.runs);
  /* The foot's "Researched ⟨date⟩" is stamped from `completedResearch` — the
     kind-filtered read directly above — and not from "the latest completed run of
     any kind", which is what it used to read. That line is a statement about how
     fresh the **facts** are, and a prose rerun captures nothing: restamping it
     would tell the office the record was re-checked on a day nobody looked. It is
     the one item in §6.3's sweep that is a data fix rather than a wording, and it
     was already latent for a quick search — a rewrite just makes it routine. */

  /* The open questions the rewrite is blocked on (§6.2). Read off the record's
     own `pending_decisions`, which is already in hand — the same subject-scoped
     pending read the server refuses creation on (`pendingForSubject`), so the
     disabled button and the 409 behind it answer the same question from the same
     rows rather than from two predicates that could drift.

     Subject-scoped rather than run-scoped on purpose, and it is the whole point:
     since §3f a *live* run with open questions is parked non-terminal and the
     rewrite is already refused as `RUN_ALREADY_ACTIVE`. What this still catches
     is the queued row that outlived a terminal run — answerable inline on the
     brief, with nothing waiting on it, and exactly the case where prose written
     now is prose to rewrite again.

     `blocking === false` for that same reason, and it is the server's predicate
     verbatim: a blocking row still pending on a terminal run has no answerable
     surface left anywhere, so counting it would gray the button out forever. */
  const openQuestionCount = record.pending_decisions.filter(
    (decision) => decision.kind === 'claim_adjudication' && decision.blocking === false,
  ).length;

  const updateBlocker = updateBriefBlocker(openQuestionCount, record.notes);
  const unreadNotes = notesSinceBrief(record.notes, analyses);
  const notesChanged = notesChangedSinceBrief(record.notes_changed_at, analyses);

  /* The foot's three controls each open a modal; only one is open at a time. */
  const [foot, setFoot] = useState<'runs' | 'history' | 'refresh' | null>(null);
  const closeFoot = () => setFoot(null);
  const canRefresh = Boolean(completedResearch && identity);
  const useSectionReader =
    Boolean(completedResearch) || hasDetailedReading || notableFacts(record.facts).length > 0;

  // A failed run the server would resume holds the tab for its Resume, queued or
  // not; one Resume refuses gives it back to the button that starts a run.
  const { canKickOff, canRunDossier } = newRunOffers({
    latestRun,
    identity: Boolean(identity),
    completedResearch: Boolean(completedResearch),
    demo,
  });

  /* Delete the person. It is present whatever state the record is in — a
     half-broken record is the case it exists for — but not while work is
     actually in flight. The server takes a delete mid-run and fails the run
     itself; the office still should not reach for it while the strip is
     moving, so the working states — `created`, `running`, and `checkpointed`,
     which is between legs with the next one already enqueued — take the control
     away and the waiting states leave it. The polled run is the truth when there
     is one. */
  const liveState = runQuery.data?.state ?? latestRun?.state ?? null;
  const runMoving = liveState !== null && isWorkingState(liveState);
  const deleteSubject = useDeleteSubject(subjectId);
  const [deleting, setDeleting] = useState(false);

  /* **"Brief ready" shows once, on the live transition** (blend spec, "Run
     complete"). The page remembers the last live run it watched; when the
     record then reads that same run as complete, the confirmation stands for a
     few seconds and gives way to the brief. A record that was already complete
     when the page opened never shows it: nothing was watched finishing. */
  const lastLive = useRef<RunDetail | null>(null);
  const [finished, setFinished] = useState<{
    subjectId: string;
    run: RunDetail;
    completedAt: string | null;
    delivered: boolean | null;
    leaving: boolean;
  } | null>(null);
  useEffect(() => {
    if (liveRunId && runQuery.data?.id === liveRunId) {
      lastLive.current = runQuery.data;
      return;
    }
    const seen = lastLive.current;
    lastLive.current = null;
    if (seen && latestRun && finishedWhileWatching(seen, latestRun)) {
      setFinished({
        subjectId,
        run: seen,
        completedAt: latestRun.completed_at,
        delivered: latestRun.delivered,
        leaving: false,
      });
    }
  }, [liveRunId, runQuery.data, latestRun, subjectId]);
  const finishedId = finished?.run.id ?? null;
  useEffect(() => {
    if (!finishedId) return;
    const leave = setTimeout(
      () => setFinished((current) => (current ? { ...current, leaving: true } : current)),
      RUN_COMPLETE_SHOWN_MS,
    );
    const gone = setTimeout(() => setFinished(null), RUN_COMPLETE_SHOWN_MS + 520);
    return () => {
      clearTimeout(leave);
      clearTimeout(gone);
    };
  }, [finishedId]);

  const liveResearch =
    liveRun && liveRun.kind === 'research' && !isTerminal(liveRun.state) ? liveRun : null;
  const draft = brief.data?.view === 'draft';

  const lead = (
    <>
      {/* Which brief is below, and the way across. It names what the reader
          is reading; the run block narrates the run. */}
      {brief.data ? (
        <BriefViewNote
          view={brief.data.view}
          asOfCompletedAt={brief.data.as_of_completed_at}
          canPin={Boolean(completedResearch)}
          onView={setBriefView}
        />
      ) : null}

      {canKickOff ? (
        <div className="hint">
          {/* **The identifiers, where there are some, and the plain fact that
              they are not an identity** (owner's ruling, 2026-09-15: an import
              does not establish identity). An imported person carries the club's
              own confirmed email and member number, and a line reading only
              "identity confirmation pending" would leave out the half that is
              already true. What they are not is a confirmed identity, and the
              button below is what settles that. */}
          {restartLine ? <div className="flag amber row">{restartLine}</div> : null}
          <div>
            {record.anchors_on_record.length > 0
              ? `${plural(
                  record.anchors_on_record.length,
                  'identifier',
                  'identifiers',
                )} on record. Identity is not yet confirmed.`
              : 'Identity is not yet confirmed.'}
          </div>
          <div className="note-actions">
            <WorkingButton
              label={pricedLabel(
                autoContinue ? 'Run quick search and research' : 'Run quick search',
                kickoffPrice,
                credits.data?.charging_enabled ? credits.data.units_per_credit : undefined,
              )}
              workingLabel="Starting…"
              working={createRun.isPending}
              disabled={kickoffShort !== null}
              onClick={() =>
                createRun.mutate({
                  kind: 'quick_search',
                  config: { auto_continue: autoContinue ? 'research' : 'none' },
                  /* Enrolled accounts only: the price on the button is the price
                     accepted, and the key makes a retried press the same press. */
                  ...(kickoffPrice === undefined
                    ? {}
                    : {
                        admission_key: crypto.randomUUID(),
                        expected_credit_units: kickoffPrice ?? 0,
                      }),
                })
              }
            />
            {kickoffShort ? <span className="credit-short">{kickoffShort}</span> : null}
          </div>
          <Checkbox className="picker-option" checked={autoContinue} onChange={setAutoContinue}>
            Continue with research after confirming identity.
          </Checkbox>
          {createRun.isError ? <ErrorLine error={createRun.error} /> : null}
        </div>
      ) : null}

      {/* The first run has no finished brief behind it, so there is nothing to
          pin to and nothing to toggle — only the plain fact that what follows is
          still being written. */}
      {brief.data ? (
        <DraftInProgressNote
          view={brief.data.view}
          canPin={Boolean(completedResearch)}
          moving={runUnderway}
        />
      ) : null}

      {queuedAnchor ? (
        <p className="topline needs-review">
          <a className="link" href={queuedAnchor}>
            Needs review · {plural(queuedCount, 'finding', 'findings')}
          </a>
        </p>
      ) : null}

      {summary && !useSectionReader ? (
        <>
          <div className="reading">
            <Prose text={summary} />
          </div>
          <Caveats caveats={analysisCaveats(personSummary)} />
        </>
      ) : null}

      {/* The line the brief's heads used to stand in for, where the panel
          would be — so the way on reads directly under it. */}
      {neverResearched && identity ? <EmptyLine>No research yet.</EmptyLine> : null}

      {/* "Run research", not "Run the full dossier": a member's and a contact's
          run is a light pass over three sections, so the old label promised a
          depth this button cannot deliver. Same ruling as RUN_KIND_LABELS.research.
          It stays under the quick search's summary, where the reader finishes,
          as its own block: a new run, priced, not a step inside the panel. */}
      {canRunDossier ? (
        <div className="run-next">
          <WorkingButton
            label={pricedLabel('Run research', researchPrice.units, researchPrice.unitsPerCredit)}
            workingLabel="Starting…"
            working={createRun.isPending}
            disabled={researchShort !== null}
            onClick={() =>
              createRun.mutate({
                kind: 'research',
                ...(researchPrice.units === undefined
                  ? {}
                  : {
                      admission_key: crypto.randomUUID(),
                      expected_credit_units: researchPrice.units ?? 0,
                    }),
              })
            }
          />
          {researchShort ? <span className="credit-short">{researchShort}</span> : null}
          {createRun.isError ? <ErrorLine error={createRun.error} /> : null}
        </div>
      ) : null}

      <FlaggedFindings
        key={subjectId}
        rows={flaggedRows}
        subjectId={subjectId}
        subjectName={record.subject.name}
        runId={liveRunId}
      />
      {!demo && brief.data && brief.data.queued_overflow_count > 0 ? (
        <div className="src spaced">
          {plural(
            brief.data.queued_overflow_count,
            'more flagged finding is',
            'more flagged findings are',
          )}{' '}
          not listed for review.
        </div>
      ) : null}
    </>
  );

  const sources = useSourceIndex(subjectId, hasRecord);

  const aside = (
    <ResearchAside
      record={record}
      subjectId={subjectId}
      research={researchState(liveRun, latestRun, restartOnly, completedResearch ?? null)}
    />
  );

  return (
    <div className="research-tab">
      {liveRunId && runQuery.data ? (
        <IfWrites>
          <RunBlock
            subjectId={subjectId}
            run={runQuery.data}
            pendingDecisions={record.pending_decisions}
            pollFailures={runQuery.failureCount}
          />
        </IfWrites>
      ) : finished && finished.subjectId === subjectId ? (
        <IfWrites>
          <RunCompleteBlock
            kind={finished.run.kind}
            delivered={finished.delivered}
            startedAt={finished.run.created_at}
            finishedAt={finished.completedAt}
            tail={plural(
              record.facts.filter((fact) => fact.status === 'active').length,
              'fact on record',
              'facts on record',
            )}
            leaving={finished.leaving}
          />
        </IfWrites>
      ) : personSummary && identity && (unreadNotes > 0 || notesChanged) ? (
        <IfWrites>
          <NotesSinceBrief
            subjectId={subjectId}
            count={unreadNotes}
            changed={notesChanged}
            briefWrittenAt={personSummary.created_at}
            blocker={updateBlocker}
          />
        </IfWrites>
      ) : null}

      {useSectionReader ? (
        <ResearchReading
          key={`${subjectId}-${hashSection ?? ''}`}
          subject={record.subject}
          subjectId={subjectId}
          subjectName={record.subject.name}
          facts={record.facts}
          heldClaims={record.held_claims}
          analyses={analyses}
          queuedByLineage={queuedByLineage}
          runId={liveRunId}
          initialOpen={hashSection}
          draft={draft}
          sources={sources}
          lead={lead}
          aside={aside}
          plannedFacets={liveResearch?.facet_map.planned ?? []}
          briefLater={
            liveResearch
              ? liveResearch.state === 'awaiting_adjudication'
                ? SECTION_NAV.briefAfterReview
                : SECTION_NAV.briefLater
              : null
          }
        />
      ) : (
        <ResearchGrid aside={aside}>
          {lead}
          {hasRecord ? (
            <div className="research-panels stacked">
              <FactSections
                subjectId={subjectId}
                subjectName={record.subject.name}
                facts={record.facts}
                heldClaims={record.held_claims}
                analyses={analyses}
                queuedByLineage={queuedByLineage}
                runId={liveRunId}
                initialOpen={hashSection}
                draft={draft}
                sources={sources}
              />
            </div>
          ) : null}
        </ResearchGrid>
      )}

      {completedResearch ? (
        <LaterRuns subjectId={subjectId} subjectName={record.subject.name} facts={record.facts} />
      ) : null}

      <ResearchFooter
        stamp={joinClauses([
          completedResearch?.completed_at
            ? `Researched ${formatDay(completedResearch.completed_at)}`
            : null,
          `last updated ${formatDay(record.subject.updated_at)}`,
        ])}
        onRuns={() => setFoot('runs')}
        onHistory={() => setFoot('history')}
        onRefresh={canRefresh ? () => setFoot('refresh') : undefined}
        rawData={
          <Link to={`/people/${subjectId}/data`} className="foot-link">
            {RAW_DATA_ACTION}
          </Link>
        }
        deleteAction={
          <DangerLink disabled={runMoving} onClick={() => setDeleting(true)}>
            Delete person
          </DangerLink>
        }
      />

      {foot === 'runs' ? (
        <Modal label="Runs" wide onClose={closeFoot}>
          <div className="modal-head">Runs</div>
          <div className="modal-body">
            <RunHistory subjectId={subjectId} />
          </div>
          <div className="modal-actions">
            <Button variant="act" onClick={closeFoot}>
              Close
            </Button>
          </div>
        </Modal>
      ) : null}

      {foot === 'history' ? (
        <Modal label="Decision log" wide onClose={closeFoot}>
          <div className="modal-head">Decision log</div>
          <div className="modal-body">
            <DecisionLog subjectId={subjectId} />
          </div>
          <div className="modal-actions">
            <Button variant="act" onClick={closeFoot}>
              Close
            </Button>
          </div>
        </Modal>
      ) : null}

      {deleting ? (
        <ConfirmModal
          title="Delete person"
          line={
            <>
              This deletes {record.subject.name} and everything on record for them.{' '}
              {CANNOT_BE_UNDONE}
            </>
          }
          confirmLabel="Delete"
          workingLabel="Deleting…"
          danger
          working={deleteSubject.isPending}
          onConfirm={() => deleteSubject.mutate()}
          dismissLabel="Cancel"
          onClose={() => setDeleting(false)}
        >
          {deleteSubject.isError ? <ErrorLine error={deleteSubject.error} /> : null}
        </ConfirmModal>
      ) : null}

      {foot === 'refresh' && completedResearch && identity ? (
        <Modal label="Refresh" onClose={closeFoot}>
          <div className="modal-head">Refresh</div>
          <DossierRefresh
            subjectId={subjectId}
            since={completedResearch.completed_at ?? completedResearch.created_at}
            updateBlocker={updateBlocker}
            hasClubFit={analyses.some((analysis) => analysis.kind === 'club_fit')}
            onClose={closeFoot}
          />
        </Modal>
      ) : null}
    </div>
  );
}
